import {
    convertNetworkTypeToTelemetryType,
    IPVersion,
    GdprLevel,
    ErrorDetails,
    GetHexString,
    RequestHttpOptions,
    performHttpRequest,
    Response,
    TelemetryEventPayload,
    Log,
    FormattedEventDetail
} from "../dependencies";
import {
    RagnarokTelemetryConfig,
    TelemetryEventDbSchema,
    Streamer_ExitDef,
    Connectivity
} from "./telemetryinterfaces";
import { StreamExitEventData } from "../rinterfaces";
import { EventDataElements, JsEventsConfig } from "../interfaces";
import { IndexedDb, StoreDetails } from "../util/indexdb";
import { RagnarokSettings } from "../util/settings";
import { toTelemetryVideoCodecType, ToResumeType } from "../util/utils";
import { NetworkDetector } from "../util/networkdetector";
import { VideoCodecType } from "../nskinterfaces";

const LOGTAG = "telemetryeventprocessor";

const EXIT_EVENT_STORE_NAME = "exit-events";
const TELEMETRY_EVENTS_STORE_NAME = "telemetry-events";

export declare interface exceptionHandlerCallbackType {
    (
        error: Error | DOMException | undefined,
        msg: string,
        file: string,
        lineno: number,
        colno: number,
        handled: boolean,
        category?: string
    ): void;
}

export class TelemetryEventProcessor {
    private cachedExitEvent: TelemetryEventDbSchema = {
        userId: "",
        sessionId: "",
        eventPayload: undefined,
        name: ""
    };

    private eventDataElements: EventDataElements = {};
    private exceptionHandler?: exceptionHandlerCallbackType;
    private idb: IndexedDb;
    private openPromise: Promise<void>;
    // Below 4 flags are needed to avoid infinite indexDb operation failure and exception cycle
    private cacheExitEventInDbExceptionSentOnce = false;
    private clearExitEventStoreInDbExceptionSentOnce = false;
    private cacheTelemetryEventInDbExceptionSentOnce = false;
    private clearTelemetryEventStoreInDbExceptionSentOnce = false;
    private gameShortName: string = "";
    private gameCmsId: string = "";
    private pendingTelemetry: any[] = [];
    constructor() {
        const storeDetails: StoreDetails[] = [
            {
                storeName: EXIT_EVENT_STORE_NAME,
                storeOptions: { keyPath: ["userId", "sessionId", "name"] },
                storeIndexName: "exit-event-index"
            },
            {
                storeName: TELEMETRY_EVENTS_STORE_NAME,
                storeOptions: {
                    keyPath: ["userId", "sessionId", "name", "eventPayload.ts"]
                },
                storeIndexName: "telemetry-event-index"
            }
        ];
        this.idb = new IndexedDb("ragnarok", storeDetails);
        this.openPromise = this.idb.open();
    }

    public setExceptionHandler(handler: exceptionHandlerCallbackType) {
        this.exceptionHandler = handler;
    }

    private haveConsentToSend(consent: string): boolean {
        let canSend = false;
        if (this.eventDataElements.commonData) {
            if (
                consent === GdprLevel.Functional &&
                (this.eventDataElements.commonData.deviceGdprFuncOptIn === "Full" ||
                    this.eventDataElements.commonData.deviceGdprFuncOptIn === "Temp")
            ) {
                canSend = true;
            } else if (
                consent === GdprLevel.Technical &&
                this.eventDataElements.commonData.gdprTechOptIn === "Full"
            ) {
                canSend = true;
            } else if (
                consent === GdprLevel.Behavioral &&
                this.eventDataElements.commonData.gdprBehOptIn === "Full"
            ) {
                canSend = true;
            }
        }
        return canSend;
    }

    public sendTelemetryEvent(event: TelemetryEventPayload) {
        if (!this.eventDataElements.commonData) {
            this.pendingTelemetry.push(event);
            return;
        }
        if (!this.haveConsentToSend(event.gdprLevel)) {
            return;
        }

        if (window.navigator.onLine) {
            this.sendHttpTelemetryRequest(this.constructEventDataElements(event));
        } else {
            const eventDbFormat: TelemetryEventDbSchema = {
                userId: this.eventDataElements.commonData!.userId!,
                sessionId: this.eventDataElements.commonData!.sessionId!,
                name: event.name,
                eventPayload: event
            };
            this.cacheTelemetryEventInDb(eventDbFormat);
        }
    }

    private constructEventDataElements(event: TelemetryEventPayload): EventDataElements {
        let localEventObj: EventDataElements = {};
        if (!this.eventDataElements.commonData) {
            return localEventObj;
        }
        const formattedEventDetail: FormattedEventDetail = {
            name: event.name,
            ts: event.ts!,
            parameters: event.parameters!
        };

        // Make a deep copy of common data before modifying any properties
        this.copyCommonEventDataElements(localEventObj, this.eventDataElements);
        const commonData = localEventObj.commonData!;
        // Use the incoming event's schema config details to dispatch the event
        commonData.clientId = event.clientConfig.clientId;
        commonData.eventSchemaVer = event.clientConfig.eventSchemaVer;
        commonData.sentTs = new Date().toISOString();
        commonData.events = [formattedEventDetail];
        return localEventObj;
    }

    private sendHttpTelemetryRequest(telemetryEventData: EventDataElements) {
        if (telemetryEventData.config?.server && telemetryEventData.config?.version) {
            const options: RequestHttpOptions = {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-Event-Protocol": telemetryEventData.commonData?.eventProtocol ?? "1.4"
                },
                body: JSON.stringify(telemetryEventData.commonData),
                retryCount: 3,
                timeout: 2000
            };
            Log.i("{f7c1592}", "{056661f}", options.body);
            performHttpRequest(this.getTelemetryServerUrl(telemetryEventData.config), options)
                .then((response: Response) => {})
                .catch(err => {});
        }
    }

    public setGameDetails(cmsId: string, name: string) {
        this.gameCmsId = cmsId;
        this.gameShortName = name;
    }

    public sendExitEvent(eventPayload: TelemetryEventPayload) {
        Log.d("{f7c1592}", "{01dcfe5}", eventPayload.parameters?.sessionId);
        this.updateCachedExitEventData(eventPayload);
        this.sendExitEventUsingBeacon(
            this.constructEventDataElements(this.cachedExitEvent.eventPayload!)
        );
    }

    public clearExitEventStore(pollingDone: boolean) {
        Log.d("{f7c1592}", "{badefed}", pollingDone);
        this.clearExitEventStoreInDb(pollingDone);
        this.cachedExitEvent.eventPayload = undefined;
    }

    /// Helper function to manage the EventDataElements copy. Assignment causes all these point
    // to the same reference and the cached events gets messed up.
    private copyCommonEventDataElements(target: EventDataElements, source: EventDataElements) {
        // Deep copy commonData
        target.commonData = source.commonData ? Object.assign({}, source.commonData) : undefined;

        // The following fields are common properties and will be same across all events.
        // Shallow copy is sufficient.
        target.experiments = []; // Do not send the experiments' data with the events
        target.config = source.config;
        target.telemetryEventIds = source.telemetryEventIds;
    }

    // Called by the GridApp relayed from the UI layer to update the telemetry common fields
    public updateEventDataElements(eventDataElements: EventDataElements) {
        // Make a copy of commonData so we don't modify the incoming object's references
        this.copyCommonEventDataElements(this.eventDataElements, eventDataElements);
        if (this.eventDataElements.commonData) {
            // If there are pending events, flush them
            Log.d("{f7c1592}", "{66f209c}");
            for (let event of this.pendingTelemetry) {
                this.sendTelemetryEvent(event);
            }
            this.pendingTelemetry = [];
        }
        this.cachedExitEvent.userId = this.eventDataElements.commonData?.userId!;
    }

    private updateCachedExitEventData(event: TelemetryEventPayload) {
        this.cachedExitEvent.userId = this.eventDataElements.commonData?.userId ?? "";
        this.cachedExitEvent.sessionId = event.parameters.sessionId ?? "";
        this.cachedExitEvent.name = event.name;
        this.cachedExitEvent.eventPayload = event;
    }

    public updateCachedExitEvent(
        exitErrorCode: string,
        sessionId: string,
        subSessionId: string,
        zoneAddress: string,
        streamDuration: number,
        frameCount: number,
        codec: VideoCodecType,
        isResume: boolean
    ) {
        const exitData: StreamExitEventData = {
            exitErrorCode: exitErrorCode,
            sessionId: sessionId,
            subSessionId: subSessionId,
            zoneAddress: zoneAddress,
            streamDuration: streamDuration,
            frameCount: frameCount,
            codec: codec,
            isResume: isResume,
            connectivity: "",
            sleep: false,
            networkTestSessionId: ""
        };
        const eventPayload = this.getStreamerExitEventPayload(exitData);
        this.updateCachedExitEventData(eventPayload);
    }

    public getStreamerExitEventPayload(exitData: StreamExitEventData): TelemetryEventPayload {
        let exitReason = exitData.exitErrorCode ?? "";
        if (exitData.sleep) {
            exitReason = "SleepExit";
        } else if (exitData.connectivity?.startsWith(Connectivity.ONLINE)) {
            exitReason = exitData.connectivity ?? "";
        }
        const event: Streamer_ExitDef = new Streamer_ExitDef({
            zoneAddress: exitData.zoneAddress,
            networkSessionId: exitData.networkTestSessionId ?? "",
            sessionId: exitData.sessionId,
            subSessionId: exitData.subSessionId,
            resumeType: ToResumeType(exitData.isResume),
            overrideConfigType: RagnarokSettings.remoteOverrideInfo.type,
            overrideConfigVersion: RagnarokSettings.remoteOverrideInfo.version,
            exitReason: exitReason,
            result: exitData.exitErrorCode,
            frameCount: exitData.frameCount,
            codec: toTelemetryVideoCodecType(exitData.codec),
            ipVersion: IPVersion.UNKNOWN,
            streamDuration: Math.round(exitData.streamDuration),
            networkType: convertNetworkTypeToTelemetryType(NetworkDetector.getNetworkType()),
            streamingProfileGuid: this.getStreamingProfileGuid(),
            systemInfoGuid: this.getSystemInfoGuid(),
            cmsId: String(this.gameCmsId)
        });
        return {
            name: event.name,
            gdprLevel: event.gdprLevel,
            parameters: event.parameters,
            ts: event.ts,
            clientConfig: RagnarokTelemetryConfig
        };
    }

    private handleCatch(err: ErrorDetails, funcName: string, shouldSendException: boolean) {
        Log.e("{f7c1592}", "{146ec91}", funcName, GetHexString(err.code), err.description);
        if (shouldSendException) {
            const msg = err.error?.message ?? err.description ?? `Exception in ${funcName}`;
            const filename = LOGTAG + ".ts";
            if (this.exceptionHandler) {
                this.exceptionHandler(err.error, msg, filename, err.code, 0, true, err.error?.name);
            } else {
                Log.w("{f7c1592}", "{12e0846}");
            }
        }
    }

    private isPrimaryKeyValid(telemetryEvent: TelemetryEventDbSchema): boolean {
        return !!telemetryEvent.userId && !!telemetryEvent.sessionId && !!telemetryEvent.name;
    }

    private cacheTelemetryEventInDb(cachedTelemetryEvent: TelemetryEventDbSchema) {
        if (!this.isPrimaryKeyValid(cachedTelemetryEvent)) {
            return;
        }
        // intentionally resolving it, as we want to not block anything
        this.openPromise
            .then(() => this.idb.set(TELEMETRY_EVENTS_STORE_NAME, cachedTelemetryEvent))
            .then(() => {
                Log.i("{f7c1592}", "{f03d70b}");
            })
            .catch((err: ErrorDetails) => {
                // only try to send an exception if we have not tried it yet, to prevent an infinite loop
                const shouldHandleException = !this.cacheTelemetryEventInDbExceptionSentOnce;
                this.cacheTelemetryEventInDbExceptionSentOnce = true;
                this.handleCatch(err, "cacheTelemetryEventInDb", shouldHandleException);
            });
    }

    public cacheExitEventInDb(): Promise<void> {
        if (!this.isPrimaryKeyValid(this.cachedExitEvent)) {
            return Promise.resolve();
        }
        // intentionally resolving it, as we want to not block anything
        return this.openPromise
            .then(() => this.idb.set(EXIT_EVENT_STORE_NAME, this.cachedExitEvent))
            .then(() => {
                Log.i("{f7c1592}", "{5d026f8}");
            })
            .catch((err: ErrorDetails) => {
                this.handleCatch(
                    err,
                    "cacheExitEventInDb",
                    !this.cacheExitEventInDbExceptionSentOnce
                );
                if (!this.cacheExitEventInDbExceptionSentOnce) {
                    this.cacheExitEventInDbExceptionSentOnce = true;
                }
            });
    }

    private getTelemetryServerUrl(config: JsEventsConfig): string {
        return config.server + "/" + config.version + "/events/json";
    }

    private sendExitEventUsingBeacon(eventDataElements: EventDataElements) {
        if (
            eventDataElements.config?.server &&
            eventDataElements.config?.version &&
            eventDataElements.experiments
        ) {
            if (eventDataElements.commonData) {
                eventDataElements.commonData.sentTs = new Date().toISOString();
            }
            const blobData = JSON.stringify(eventDataElements.commonData);
            const formData = new Blob([blobData], {
                type: "text/plain"
            });
            let result = false;
            result = navigator.sendBeacon(
                this.getTelemetryServerUrl(eventDataElements.config),
                formData
            );
            Log.i("{f7c1592}", "{b9367c5}", result, blobData);
        }
    }

    private clearExitEventStoreInDb(pollingDone: boolean) {
        if (pollingDone) {
            // This is little tricky, we don't want to unintentionally clear cache with valid case.
            // we need to think about multi tab, launch error in next session, error in current session during stream cases etc
            this.openPromise
                .then(() => this.idb.clear(EXIT_EVENT_STORE_NAME))
                .then(() => {
                    Log.i("{f7c1592}", "{1b8f127}");
                })
                .catch((err: ErrorDetails) => {
                    this.handleCatch(
                        err,
                        "clearExitEventStoreInDb",
                        !this.clearExitEventStoreInDbExceptionSentOnce
                    );
                    if (!this.clearExitEventStoreInDbExceptionSentOnce) {
                        this.clearExitEventStoreInDbExceptionSentOnce = true;
                    }
                });
        }
    }

    public getCachedExitEvents(): Promise<TelemetryEventPayload[]> {
        return this.openPromise
            .then(() => this.idb.getAll(EXIT_EVENT_STORE_NAME))
            .then((exitEvents: TelemetryEventDbSchema[]) => {
                Log.i("{f7c1592}", "{d938300}", exitEvents.length);
                let eventPayloads: TelemetryEventPayload[] = [];
                if (exitEvents.length > 0) {
                    for (const telemetryEvent of exitEvents) {
                        if (telemetryEvent.eventPayload) {
                            eventPayloads.push(telemetryEvent.eventPayload);
                        }
                    }
                }
                return eventPayloads;
            })
            .catch((err: ErrorDetails) => {
                this.handleCatch(err, "getCachedExitEvents", true);
                return Promise.reject();
            });
    }

    public sendCachedExitEvent(pollingDone: boolean): Promise<void> {
        // intentionally always resolve, as we don't want to block anything
        return this.openPromise
            .then(() => this.idb.getAll(EXIT_EVENT_STORE_NAME))
            .then((exitEvents: TelemetryEventDbSchema[]) => {
                Log.i("{f7c1592}", "{ca568ff}", exitEvents.length);
                if (exitEvents.length > 0) {
                    for (const telemetryEvent of exitEvents) {
                        if (telemetryEvent.eventPayload) {
                            this.sendExitEventUsingBeacon(
                                this.constructEventDataElements(telemetryEvent.eventPayload)
                            );
                        }
                    }
                    this.clearExitEventStore(pollingDone);
                }
            })
            .catch((err: ErrorDetails) => {
                this.handleCatch(err, "sendCachedExitEvent", true);
            });
    }

    private clearTelemetryEventStoreInDb() {
        this.openPromise
            .then(() => this.idb.clear(TELEMETRY_EVENTS_STORE_NAME))
            .then(() => {
                Log.i("{f7c1592}", "{8f62936}");
            })
            .catch((err: ErrorDetails) => {
                this.handleCatch(
                    err,
                    "clearEventStoreInDb",
                    !this.clearTelemetryEventStoreInDbExceptionSentOnce
                );
                if (!this.clearTelemetryEventStoreInDbExceptionSentOnce) {
                    this.clearTelemetryEventStoreInDbExceptionSentOnce = true;
                }
            });
    }

    public sendAllCachedTelemetryEvents() {
        if (window.navigator.onLine) {
            this.openPromise
                .then(() => this.idb.getAll(TELEMETRY_EVENTS_STORE_NAME))
                .then((telemetryEvents: TelemetryEventDbSchema[]) => {
                    Log.i("{f7c1592}", "{d4e574c}", telemetryEvents.length);
                    if (telemetryEvents.length > 0) {
                        this.clearTelemetryEventStoreInDb();
                        for (const telemetryEvent of telemetryEvents) {
                            if (telemetryEvent.eventPayload) {
                                this.sendTelemetryEvent(telemetryEvent.eventPayload);
                            }
                        }
                    }
                })
                .catch((err: ErrorDetails) => {
                    Log.e("{f7c1592}", "{aaaa3de}", err.code, err.description);
                });
        }
    }

    public resetDataOnNewSubSession(sessionId: string, subSessionId: string) {
        this.cacheExitEventInDbExceptionSentOnce = false;
        this.clearExitEventStoreInDbExceptionSentOnce = false;
        this.cacheTelemetryEventInDbExceptionSentOnce = false;
        this.clearTelemetryEventStoreInDbExceptionSentOnce = false;
    }

    public getStreamingProfileGuid() {
        return this.eventDataElements?.telemetryEventIds?.streamingProfileGuid ?? "";
    }

    public getSystemInfoGuid() {
        return this.eventDataElements?.telemetryEventIds?.systemInfoGuid ?? "";
    }
}
